import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const { api } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await api.post('/auth/forgot-password', { email });
      setIsSuccess(true);
    } catch (error) {
      console.error('Forgot password error:', error);
      if (error.response?.status === 404) {
        setError('Aucun compte trouvé avec cette adresse email');
      } else if (error.response?.status === 429) {
        setError('Trop de tentatives. Réessayez dans quelques minutes.');
      } else {
        setError('Erreur lors de l\'envoi. Veuillez réessayer.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap');`}
      </style>
      
      <div 
        className="min-h-screen flex flex-col px-4 py-6 relative overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #B8E0E8 0%, #C5E8D8 50%, #D4EED0 100%)'
        }}
      >
        {/* Header avec bulle et mascotte */}
        <div className="flex justify-between items-start mb-4">
          {/* Bulle "Dis Maman !" */}
          <Link to="/" className="relative">
            <svg 
              viewBox="0 0 200 80" 
              className="w-[140px] sm:w-[180px] h-auto"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
            >
              <path 
                d="M 15,40 
                   C 15,15 35,5 70,5 
                   C 110,3 150,8 175,18 
                   C 192,25 197,40 192,55 
                   C 188,68 165,75 130,78 
                   C 95,81 55,78 30,68
                   L 10,82
                   L 20,65
                   C 13,60 15,50 15,40 Z"
                fill="white"
                stroke="#374151"
                strokeWidth="2.5"
              />
            </svg>
            <p 
              className="absolute top-1/2 left-1/2 text-xl sm:text-2xl font-bold text-gray-800 whitespace-nowrap"
              style={{ 
                fontFamily: '"Caveat", cursive',
                transform: 'translate(-52%, -50%) rotate(-1deg)'
              }}
            >
              Dis Maman !
            </p>
          </Link>

          {/* Mascotte */}
          <div className="relative -mt-2 -mr-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_7b59aa73-04f7-469e-b3b0-3b5db031400b/artifacts/0zxv9crx_02D61451-5FF5-40EF-9C50-17F42BA59024.png"
              alt="Mascotte"
              className="h-[120px] sm:h-[160px] object-contain"
              style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))' }}
            />
          </div>
        </div>

        {/* Formulaire dans une carte blanche */}
        <div className="bg-white rounded-3xl p-6 shadow-lg mx-auto w-full max-w-md">
          {!isSuccess ? (
            <>
              {/* Header du formulaire */}
              <div className="text-center mb-6">
                <div className="text-4xl mb-2">🔑</div>
                <h1 
                  className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1"
                  style={{ fontFamily: '"Caveat", cursive' }}
                >
                  Réinitialiser le mot de passe
                </h1>
                <p className="text-gray-500 text-sm">
                  Entrez votre email pour recevoir un lien de réinitialisation
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1 text-center">
                    ✉️ Adresse email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre.email@example.com"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-teal-400 focus:outline-none transition-colors bg-gray-50"
                    required
                    data-testid="email-input"
                  />
                </div>

                {/* Erreur */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm text-center">
                    {error}
                  </div>
                )}

                {/* Bouton submit */}
                <button
                  type="submit"
                  disabled={isLoading || !email}
                  className="w-full py-4 px-6 rounded-2xl text-white font-bold text-lg flex items-center justify-center gap-2 shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70"
                  style={{
                    background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)'
                  }}
                  data-testid="submit-btn"
                >
                  {isLoading ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      🚀 Envoyer le lien
                    </>
                  )}
                </button>
              </form>

              {/* Liens retour */}
              <div className="text-center mt-4 space-y-2">
                <Link 
                  to="/login" 
                  className="block text-gray-600 text-sm hover:text-gray-800"
                >
                  ← Retour à la connexion
                </Link>
                <Link 
                  to="/" 
                  className="block text-gray-600 text-sm hover:text-gray-800"
                >
                  ← Retour à l'accueil
                </Link>
              </div>
            </>
          ) : (
            /* Message de succès */
            <div className="text-center space-y-4">
              <div className="text-5xl mb-4">✅</div>
              <h2 
                className="text-2xl font-bold text-green-600 mb-2"
                style={{ fontFamily: '"Caveat", cursive' }}
              >
                Email envoyé !
              </h2>
              <p className="text-gray-600 text-sm">
                Nous avons envoyé un lien de réinitialisation à <strong>{email}</strong>.
              </p>
              <p className="text-gray-500 text-sm">
                Vérifiez votre boîte mail (et vos spams) !
              </p>
              
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-xl text-sm">
                💡 Le lien sera valide pendant 1 heure
              </div>
              
              <Link 
                to="/login" 
                className="inline-block text-teal-600 font-bold hover:text-teal-700 mt-4"
              >
                Retour à la connexion →
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ForgotPasswordPage;
